import React from "react";

const ROOM_IMAGE = "https://images.unsplash.com/photo-1704655295066-681e61ecca6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjbGFzc2ljJTIwZGFyayUyMHdvb2QlMjBvZmZpY2UlMjBkZXNrfGVufDB8fHx8MTc4MTI0ODYzOXww&ixlib=rb-4.1.0&q=85";

const ADVOCATES = [
  {
    initials: "MK",
    name: "M.K. Sikdar",
    role: "Founder | Advocate · 27+ years at the Bar",
    bio: "M.K. Sikdar is the Founder of Sikdar & Sikdar — Advocates and has over 27 years of legal practice. He has represented individuals, businesses and institutions across a wide range of civil, commercial, property, family and service law matters. His extensive courtroom experience, strategic insight and commitment to professional excellence continue to guide the Chamber's approach to legal practice.",
    tags: ["Civil", "Commercial", "Property", "Family", "Service"],
  },
  {
    initials: "KS",
    name: "Kirti Sikdar",
    role: "Advocate",
    bio: "Kirti Sikdar practices at Sikdar & Sikdar — Advocates with 4+ years of litigation and advisory experience. Her practice focuses on civil, commercial, constitutional, property, service, family and consumer law matters. She is committed to delivering practical legal solutions through meticulous research, strategic advocacy and personalised client representation.",
    tags: ["Civil", "Commercial", "Constitutional", "Property", "Service", "Family", "Consumer"],
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="section paper-grain">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <span className="ornament text-xs" style={{ letterSpacing: "0.28em", textTransform: "uppercase" }}>
              Welcome to the Chamber
            </span>
            <h2
              className="font-serif-display mt-5 md:mt-6 text-3xl sm:text-4xl md:text-5xl"
              style={{ color: "#0A1128", fontWeight: 500, lineHeight: 1.15 }}
            >
              Legal excellence built on
              <br />
              <span style={{ fontStyle: "italic", color: "#7A2021" }}>
                experience, integrity &amp; care.
              </span>
            </h2>

            <div className="mt-8 md:mt-10 overflow-hidden hidden md:block" style={{ border: "1px solid #E2DCD0" }}>
              <img
                src={ROOM_IMAGE}
                alt="Dark wooden advocate's desk at the office of Sikdar & Sikdar, Advocates, Chennai"
                className="w-full h-56 object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="md:col-span-7 md:pl-8">
            <div className="space-y-5 md:space-y-6 mt-8 md:mt-0 text-base md:text-lg" style={{ color: "#334155", lineHeight: 1.8 }}>
              <p>
                Welcome to <strong style={{ color: "#0A1128" }}>Sikdar &amp; Sikdar — Advocates</strong>,
                where legal excellence is built upon experience, integrity and personalised
                representation.
              </p>
              <p>
                Founded by <strong style={{ color: "#0A1128" }}>M.K. Sikdar</strong>, the Chamber
                is dedicated to providing strategic legal advice and effective representation across
                a broad spectrum of civil, commercial, constitutional, property, family, service,
                consumer and regulatory matters.
              </p>
              <p>
                Every legal dispute presents its own unique challenges. We believe successful
                representation begins with understanding the client&apos;s objectives, analysing
                the legal issues with precision, and developing practical strategies tailored to
                the facts of each case.
              </p>
              <p>
                We combine decades of courtroom experience with meticulous legal research,
                thoughtful advocacy, and a commitment to achieving effective solutions for our
                clients. Every matter entrusted to the Chamber receives the direct attention of
                the advocate handling it — ensuring continuity, accountability and personalised
                legal service throughout the course of representation.
              </p>
              <p>
                Whether appearing before courts and tribunals or providing advisory and
                documentation services, we remain committed to the principles of professionalism,
                confidentiality, integrity and excellence that define our practice.
              </p>
            </div>

            <div className="mt-12 space-y-10">
              <div className="flex items-center gap-4">
                <span className="ornament text-xs" style={{ letterSpacing: "0.28em", textTransform: "uppercase" }}>
                  The Team
                </span>
              </div>
              {ADVOCATES.map((a) => (
                <AdvocateCard key={a.initials} advocate={a} />
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-10 mt-10" style={{ borderTop: "1px solid #E2DCD0" }}>
              <Credential title="Forums" body="High Courts of Madras, Telangana &amp; Kerala · Armed Forces Tribunals at Chennai, Hyderabad, Kochi &amp; Mumbai · Subordinate courts" />
              <Credential title="Engagement" body="Retainers · Single briefs · Documentation · Consultation &amp; Opinions" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AdvocateCard({ advocate }) {
  return (
    <div
      className="p-8 md:p-10 flex flex-col gap-5"
      style={{ backgroundColor: "#FFFFFF", border: "1px solid #E2DCD0" }}
      data-testid={`advocate-${advocate.initials.toLowerCase()}`}
    >
      <div className="flex items-center gap-5">
        <div
          className="flex items-center justify-center shrink-0 font-serif-display text-2xl"
          style={{
            width: 64,
            height: 64,
            backgroundColor: "#0A1128",
            color: "#F9F6F0",
            letterSpacing: "0.04em",
          }}
        >
          {advocate.initials}
        </div>
        <div>
          <h3 className="font-serif-display text-3xl" style={{ color: "#0A1128", fontWeight: 500, lineHeight: 1.1 }}>
            {advocate.name}
          </h3>
          <div className="mt-1 text-[11px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.22em" }}>
            {advocate.role}
          </div>
        </div>
      </div>
      <p className="text-base" style={{ color: "#334155", lineHeight: 1.7 }}>
        {advocate.bio}
      </p>
    </div>
  );
}

function Credential({ title, body }) {
  return (
    <div style={{ borderLeft: "1px solid #C5A059" }} className="pl-5">
      <div className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.24em" }}>{title}</div>
      <div className="mt-2 text-sm" style={{ color: "#0A1128" }} dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  );
}
